import { useEffect, useMemo, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { listTasksByOrganization } from '../../shared/services/taskService';
import { taskKeys, buildTaskSortKey } from '../../shared/keys';
import type { TaskFilters, TaskListItem, TaskListResult, TaskSortField, SortDirection } from '../../shared/types/task';

interface UseGestaoTasksParams {
  organizationId?: string;
  filters: TaskFilters;
  sortField: TaskSortField;
  sortDirection: SortDirection;
  page: number;
  pageSize: number;
}

interface UseGestaoTasksResult {
  items: TaskListItem[];
  total: number;
  page: number;
  pageSize: number;
  isLoading: boolean;
  isFetching: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

export function useGestaoTasks(params: UseGestaoTasksParams): UseGestaoTasksResult {
  const { organizationId, filters, sortField, sortDirection, page, pageSize } = params;
  const [debouncedSearch, setDebouncedSearch] = useState(filters.search ?? '');

  useEffect(() => {
    const handler = setTimeout(() => setDebouncedSearch(filters.search ?? ''), 350);
    return () => clearTimeout(handler);
  }, [filters.search]);

  const normalizedFilters = useMemo<TaskFilters>(() => ({
    ...filters,
    search: debouncedSearch,
  }), [debouncedSearch, filters.status, filters.priority, filters.overdue, filters.assignee, filters.stageName]);

  const filtersHash = useMemo(
    () => JSON.stringify({
      status: normalizedFilters.status,
      priority: normalizedFilters.priority,
      search: normalizedFilters.search,
      overdue: normalizedFilters.overdue,
      assignee: normalizedFilters.assignee ?? null,
      stageName: normalizedFilters.stageName ?? null,
    }),
    [normalizedFilters],
  );

  const sortKey = useMemo(() => buildTaskSortKey(sortField, sortDirection), [sortField, sortDirection]);

  const query = useQuery<TaskListResult, Error>({
    queryKey: taskKeys.byOrg(organizationId ?? '', filtersHash, sortKey, page, pageSize),
    enabled: Boolean(organizationId),
    placeholderData: keepPreviousData,
    queryFn: () => listTasksByOrganization(organizationId!, normalizedFilters, sortField, sortDirection, page, pageSize),
  });

  return {
    items: query.data?.items ?? [],
    total: query.data?.total ?? 0,
    page: query.data?.page ?? page,
    pageSize: query.data?.pageSize ?? pageSize,
    isLoading: query.isPending && !query.data,
    isFetching: query.isFetching,
    error: query.error ?? null,
    refetch: async () => { await query.refetch(); },
  };
}
